/**
 * Create and edit an owner.
 *
 * The tax ID is NOT on this form. It lives behind `SensitivePanel`, which is a
 * separate request to a separate endpoint against a separate table (invariant 5).
 * Putting it here would mean the owner PATCH body could carry one, and then every
 * future field added to this form would need someone to remember that.
 *
 * ---------------------------------------------------------------------------
 * Layout
 * ---------------------------------------------------------------------------
 *
 * A profile card up top (photo, name, contact, at-a-glance pills) rather than
 * a bare heading, because a person's own edit page reads better identity-
 * first than form-first. The photo upload lives on that card, as a small
 * badge on the avatar itself — not a collapsed `<details>` at the bottom of
 * the page nobody scrolls to, which is where it used to be. Below that, the
 * page splits into the form (wider) and a sidebar (narrower) for the two
 * things that are administrative rather than editorial: the sensitive
 * record and the file list. Grouping the form's own fields under three
 * quiet labels (Contact / Mailing address / Notes) does the same job a
 * table's column headers do — it's a map of what's coming, not decoration.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useForm, useInvalidate, useList } from '@refinedev/core';
import { Camera } from 'lucide-react';
import { MICHIGAN, US_STATES, type OwnerConnectStatus } from 'shared';
import { api, ApiError } from '../../../lib/api.js';
import { useCan } from '../../../lib/capabilities.js';
import { Avatar } from '../../../components/Avatar.js';
import { DocumentList } from '../../../components/DocumentList.js';
import { PortalAccessCard } from '../../../components/PortalAccessCard.js';
import {
  Field,
  FormErrors,
  TextArea,
  TextInput,
  fieldErrors,
  humanizeEnum,
} from '../../../components/Form.js';
import { PhotoUpload } from '../../../components/PhotoUpload.js';
import { SensitivePanel } from '../../../components/SensitivePanel.js';

interface OwnerRecord {
  id: string;
  displayName: string;
  email: string;
  phone: string | null;
  contactPerson: string | null;
  mailingAddress: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
  };
  notes: string | null;
  currentPropertyCount: number;
  hasPortalAccount: boolean;
  photoUrl: string | null;
  connect: {
    status: OwnerConnectStatus;
    requirementsDue: string[];
    enrolledAt: string | null;
  };
}

interface OwnerFormState {
  displayName: string;
  email: string;
  phone: string;
  contactPerson: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  notes: string;
}

const EMPTY: OwnerFormState = {
  displayName: '',
  email: '',
  phone: '',
  contactPerson: '',
  line1: '',
  line2: '',
  city: '',
  state: MICHIGAN,
  postalCode: '',
  notes: '',
};

export function OwnerForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<OwnerFormState>(EMPTY);

  const { onFinish, query, mutation } = useForm<OwnerRecord>({
    resource: 'owners',
    action: isEdit ? 'edit' : 'create',
    ...(isEdit ? { id } : {}),
    redirect: false,
  });

  const record = query?.data?.data;

  useEffect(() => {
    if (!record) return;
    setForm({
      displayName: record.displayName,
      email: record.email,
      phone: record.phone ?? '',
      contactPerson: record.contactPerson ?? '',
      line1: record.mailingAddress.line1 ?? '',
      line2: record.mailingAddress.line2 ?? '',
      city: record.mailingAddress.city ?? '',
      state: record.mailingAddress.state ?? MICHIGAN,
      postalCode: record.mailingAddress.postalCode ?? '',
      notes: record.notes ?? '',
    });
  }, [record?.id, record]);

  const set =
    <K extends keyof OwnerFormState>(key: K) =>
    (value: OwnerFormState[K]) =>
      setForm((previous) => ({ ...previous, [key]: value }));

  const errors = fieldErrors(mutation.error);

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    // A partially-filled address is sent as null rather than as an object with holes:
    // the API's address schema requires line1, city and postalCode together, so a
    // half-address would be a 422 that reads as a mystery.
    const hasAddress = form.line1 !== '' && form.city !== '' && form.postalCode !== '';

    const payload = {
      displayName: form.displayName,
      email: form.email,
      phone: form.phone || null,
      contactPerson: form.contactPerson || null,
      mailingAddress: hasAddress
        ? {
            line1: form.line1,
            line2: form.line2 || null,
            city: form.city,
            state: form.state,
            postalCode: form.postalCode,
          }
        : null,
      notes: form.notes || null,
    };

    const saved = (await onFinish(payload)) as { data?: OwnerRecord } | undefined;
    const savedId = saved?.data?.id ?? id;
    if (savedId && !isEdit) void navigate(`/staff/owners/${savedId}/edit`);
    else if (!savedId) void navigate('/staff/owners');
  }

  if (isEdit && query?.isLoading) return <p className="muted">Loading…</p>;

  const formCard = (
    <form className="stack card" onSubmit={(event) => void submit(event)}>
      <FormErrors error={mutation.error} />

      <p className="form-section-label">Contact</p>

      <Field label="Name" name="displayName" required error={errors.displayName}>
        <TextInput
          name="displayName"
          value={form.displayName}
          onChange={set('displayName')}
          required
        />
      </Field>

      <Field label="Email" name="email" required error={errors.email}>
        <TextInput name="email" type="email" value={form.email} onChange={set('email')} required />
      </Field>

      <Field label="Phone" name="phone" error={errors.phone}>
        <TextInput name="phone" type="tel" value={form.phone} onChange={set('phone')} />
      </Field>

      <Field
        label="Contact person"
        name="contactPerson"
        error={errors.contactPerson}
        hint="For an owner that's an LLC or trust — who to reach."
      >
        <TextInput
          name="contactPerson"
          value={form.contactPerson}
          onChange={set('contactPerson')}
        />
      </Field>

      <p className="form-section-label">Mailing address</p>

      <Field label="Street" name="mailingAddress.line1" error={errors['mailingAddress.line1']}>
        <TextInput name="mailingAddress.line1" value={form.line1} onChange={set('line1')} />
      </Field>

      <Field label="Unit / suite" name="mailingAddress.line2">
        <TextInput name="mailingAddress.line2" value={form.line2} onChange={set('line2')} />
      </Field>

      <Field label="City" name="mailingAddress.city" error={errors['mailingAddress.city']}>
        <TextInput name="mailingAddress.city" value={form.city} onChange={set('city')} />
      </Field>

      <Field label="State" name="mailingAddress.state" error={errors['mailingAddress.state']}>
        <select
          id="mailingAddress.state"
          name="mailingAddress.state"
          className="field__input"
          value={form.state}
          onChange={(event) => set('state')(event.target.value)}
        >
          {US_STATES.map((state) => (
            <option key={state.code} value={state.code}>
              {state.name}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label="ZIP"
        name="mailingAddress.postalCode"
        error={errors['mailingAddress.postalCode']}
      >
        <TextInput
          name="mailingAddress.postalCode"
          value={form.postalCode}
          onChange={set('postalCode')}
        />
      </Field>

      <p className="form-section-label">Notes</p>

      <Field label="Notes" name="notes" error={errors.notes}>
        <TextArea name="notes" value={form.notes} onChange={set('notes')} />
      </Field>

      <div className="row">
        <button type="submit" className="button" disabled={mutation.isLoading}>
          {mutation.isLoading ? 'Saving…' : isEdit ? 'Save changes' : 'Create owner'}
        </button>
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/owners')}
        >
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <section className="stack">
      <div className="row">
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/owners')}
        >
          ← All owners
        </button>
      </div>

      {isEdit && record && id ? (
        <>
          <OwnerProfileCard record={record} ownerId={id} />

          <div className="person-layout">
            {formCard}
            <div className="stack">
              <PortalAccessCard
                resource="owners"
                id={id}
                hasPortalAccount={record.hasPortalAccount}
                email={record.email}
              />
              <TenantsCard ownerId={id} />
              <ConnectCard ownerId={id} connect={record.connect} />
              <SensitivePanel resource="owners" id={id} />
              <FilesCard ownerId={id} />
            </div>
          </div>
        </>
      ) : (
        <>
          <h1>Add owner</h1>
          {formCard}
        </>
      )}
    </section>
  );
}

interface OwnerTenantRow {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  photoUrl: string | null;
  unitLabel: string;
  propertyId: string;
  propertyName: string;
  leaseId: string;
  leaseStatus: string;
}

/**
 * Read-only — there is no add/remove here, unlike DependentsPanel/PetsPanel
 * on TenantForm.tsx. A resident belongs to a lease, not to an owner
 * directly; this is a rollup of existing data (owner → current ownership →
 * property → unit → active lease → tenant), not a new relationship to
 * manage. Adding or removing a resident happens through leasing, on the
 * property's own page.
 */
function TenantsCard({ ownerId }: { ownerId: string }) {
  const [tenants, setTenants] = useState<OwnerTenantRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setTenants(null);
    setError(null);
    api<OwnerTenantRow[]>(`/owners/${ownerId}/tenants`)
      .then((rows) => {
        if (!cancelled) setTenants(rows);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(caught);
      });
    return () => {
      cancelled = true;
    };
  }, [ownerId]);

  return (
    <details className="card" open={Boolean(tenants && tenants.length > 0)}>
      <summary>Tenants{tenants && tenants.length > 0 ? ` (${tenants.length})` : ''}</summary>

      <div className="stack">
        <p className="muted">Everyone currently on an active lease in a unit this owner holds.</p>

        {error ? (
          <p className="alert alert--error">
            {error instanceof Error ? error.message : 'Could not load tenants.'}
          </p>
        ) : tenants === null ? (
          <p className="muted">Loading…</p>
        ) : tenants.length === 0 ? (
          <p className="muted">No current tenants in this owner&rsquo;s properties.</p>
        ) : (
          tenants.map((tenant) => (
            <Link
              key={tenant.leaseId}
              to={`/staff/tenants/${tenant.id}/edit`}
              className="row"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Avatar name={tenant.name} photoUrl={tenant.photoUrl} />
              <div className="stack" style={{ gap: '0.1rem' }}>
                <strong>{tenant.name}</strong>
                <div className="muted" style={{ fontSize: '0.8rem' }}>
                  {tenant.unitLabel} · {tenant.propertyName} — {humanizeEnum(tenant.leaseStatus)}
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </details>
  );
}

const CONNECT_STATUS_LABEL: Record<OwnerConnectStatus, string> = {
  NOT_INVITED: 'Not invited',
  INVITED: 'Invited',
  ONBOARDING_STARTED: 'Onboarding started',
  PENDING_VERIFICATION: 'Pending verification',
  ENROLLED: 'Enrolled',
  REQUIREMENTS_DUE: 'Requirements due',
};

/**
 * Direct-deposit enrollment status, staff-facing side. Read-only status
 * badge plus a single "Invite to enroll" action — the actual enrollment
 * flow (minting Account Links, running Stripe's hosted onboarding) only
 * ever happens through the owner's own portal, never on staff's behalf. See
 * connect-service.ts's `sendOwnerEnrollmentInvite` — this button never
 * exposes a raw Account Link, only triggers an email pointing at the
 * owner's own Payouts page.
 */
function ConnectCard({ ownerId, connect }: { ownerId: string; connect: OwnerRecord['connect'] }) {
  const canInvite = useCan('owners:write');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function invite() {
    setBusy(true);
    setError(null);
    try {
      await api(`/owners/${ownerId}/connect/invite`, { method: 'POST' });
      setSent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not send the invite.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card stack">
      <h2 style={{ margin: 0 }}>Direct deposit</h2>
      <div className="row">
        <span className="pill">{CONNECT_STATUS_LABEL[connect.status]}</span>
        {connect.enrolledAt ? (
          <span className="muted" style={{ fontSize: '0.8rem' }}>
            since {connect.enrolledAt.slice(0, 10)}
          </span>
        ) : null}
      </div>
      {connect.status === 'REQUIREMENTS_DUE' && connect.requirementsDue.length > 0 ? (
        <ul className="muted" style={{ fontSize: '0.8rem', margin: 0, paddingLeft: '1.2rem' }}>
          {connect.requirementsDue.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {error ? <p className="alert alert--error">{error}</p> : null}
      {canInvite && (connect.status === 'NOT_INVITED' || connect.status === 'INVITED') ? (
        <button
          type="button"
          className="button button--quiet"
          disabled={busy || sent}
          onClick={() => void invite()}
        >
          {sent ? 'Invite sent' : busy ? 'Sending…' : 'Invite to enroll'}
        </button>
      ) : null}
    </div>
  );
}

/**
 * The file list plus a general-purpose uploader — same shape as
 * PropertyShow.tsx/UnitShow.tsx's own Files section. The profile card's
 * camera badge is a shortcut for "set this owner's photo" specifically; this
 * is for anything else attached to their record, defaulting to "Other"
 * since it isn't the photo. Both write through the same upload path, so
 * either one's `onUploaded` needs to refresh the owner detail — a staff
 * member could just as easily pick "Owner photo" from this dropdown too.
 */
function FilesCard({ ownerId }: { ownerId: string }) {
  const invalidate = useInvalidate();
  return (
    <div className="card stack">
      <h2 style={{ margin: 0 }}>Files</h2>
      <DocumentList filter={{ ownerId }} canDelete />
      <details>
        <summary>Upload a file</summary>
        <PhotoUpload
          parent={{ ownerId }}
          defaultDocumentType="OTHER"
          onUploaded={() => {
            void invalidate({ resource: 'documents', invalidates: ['list'] });
            void invalidate({ resource: 'owners', invalidates: ['detail'] });
          }}
        />
      </details>
    </div>
  );
}

/**
 * Identity at a glance: photo, name, contact, and the two facts staff check
 * most often (how many properties, whether the portal account exists).
 * `Camera` badge toggles the upload form inline — see the file header for
 * why this replaces the old buried `<details>`.
 */
function OwnerProfileCard({ record, ownerId }: { record: OwnerRecord; ownerId: string }) {
  const invalidate = useInvalidate();
  const [showUpload, setShowUpload] = useState(false);

  return (
    <div className="stack">
      <div className="card person-profile">
        <div className="person-profile__avatar">
          <Avatar name={record.displayName} photoUrl={record.photoUrl} size="lg" />
          <button
            type="button"
            className="person-profile__avatar-edit"
            onClick={() => setShowUpload((v) => !v)}
            aria-label={record.photoUrl ? 'Change photo' : 'Add a photo'}
            title={record.photoUrl ? 'Change photo' : 'Add a photo'}
          >
            <Camera size={13} strokeWidth={2.2} />
          </button>
        </div>

        <div className="person-profile__info">
          <h1>{record.displayName}</h1>
          <span className="muted">{record.email}</span>
          {record.phone ? <span className="muted">{record.phone}</span> : null}
          <div className="row" style={{ marginTop: '0.35rem' }}>
            <PropertyPill ownerId={ownerId} propertyCount={record.currentPropertyCount} />
            {record.hasPortalAccount ? (
              <span className="pill">Has login</span>
            ) : (
              <span className="muted">No login</span>
            )}
          </div>
          <span className="muted" style={{ fontSize: '0.78rem' }}>
            Ownership is granted from a property&rsquo;s page.
          </span>
        </div>
      </div>

      {showUpload ? (
        <div className="card">
          <PhotoUpload
            parent={{ ownerId }}
            defaultDocumentType="OWNER_PHOTO"
            onUploaded={() => {
              void invalidate({ resource: 'documents', invalidates: ['list'] });
              void invalidate({ resource: 'owners', invalidates: ['detail'] });
              setShowUpload(false);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

interface OwnershipSummary {
  id: string;
  propertyId: string;
  propertyName?: string;
  percent: string;
  isCurrent: boolean;
}

/**
 * The property-count pill, made to actually go somewhere — same pattern as
 * TenantForm.tsx's `LeasePill`. One current property jumps straight to it;
 * more than one opens a small menu (same positioning technique as
 * SettingsPanel.tsx's gear-icon dropdown) so staff pick which property
 * before committing to a page. `propertyCount` comes from the owner record
 * we already have, so the label doesn't wait on this component's own fetch.
 */
function PropertyPill({ ownerId, propertyCount }: { ownerId: string; propertyCount: number }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { data } = useList<OwnershipSummary>({
    resource: 'ownerships',
    filters: [{ field: 'ownerId', operator: 'eq', value: ownerId }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: propertyCount > 0 },
  });

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const label = `${propertyCount} propert${propertyCount === 1 ? 'y' : 'ies'}`;
  const title = "Ownership is granted from a property's page.";

  if (propertyCount === 0) {
    return (
      <span className="pill" title={title}>
        {label}
      </span>
    );
  }

  // `currentPropertyCount` (and this pill) counts only current ownership
  // periods — the same rows app_owner_can_see_property() matches on — so a
  // lapsed ownership on the same property doesn't inflate the count or
  // appear twice in the menu.
  const current = (data?.data ?? []).filter((o) => o.isCurrent);

  if (propertyCount === 1) {
    const only = current[0];
    return only ? (
      <Link to={`/staff/properties/${only.propertyId}`} className="pill" title={title}>
        {label}
      </Link>
    ) : (
      <span className="pill" title={title}>
        {label}
      </span>
    );
  }

  return (
    <div className="pill-menu" ref={menuRef}>
      <button
        type="button"
        className="pill pill-menu__trigger"
        title={title}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
      </button>

      {open && current.length > 0 ? (
        <div className="pill-menu__panel card">
          {current.map((ownership) => (
            <Link
              key={ownership.id}
              to={`/staff/properties/${ownership.propertyId}`}
              className="pill-menu__item"
              onClick={() => setOpen(false)}
            >
              <strong>{ownership.propertyName ?? ownership.propertyId}</strong>
              <span className="muted"> — {ownership.percent}%</span>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}
